import {useEffect,useState, useRef} from "react";
import { useParams,  useLocation } from "react-router-dom";

import API_URL from "../../../api/API";

import ServiceSelection from "../screens/ServiceSelection";
import PhoneScreen from "../screens/PhoneScreen";
import OtpScreen from "../screens/OtpScreen";
import ProfilePicker from "../screens/ProfilePicker";
import DobScreen from "../screens/DobScreen";
import GuardianConsentRequiredScreen from "../screens/GuardianConsentRequiredScreen";
import GuardianDetailsScreen from "../screens/GuardianDetailsScreen";
import GuardianWaiting from "../screens/GuardianWaiting";

import GuardianPhoneScreen from "../screens/GuardianPhoneScreen.jsx";
import GuardianOtpScreen from "../screens/GuardianOtpScreen.jsx";

import GuardianConsentScreen from "../screens/GuardianConsentScreen";
import EligibilityQuestionsScreen from "../screens/EligibilityQuestionsScreen";
import EligibilityWarningScreen from "../screens/EligibilityWarningScreen";
import EligibilityDisallowedScreen from "../screens/EligibilityDisallowedScreen";
import WaiverScreen from "../screens/WaiverScreen";
import SignatureScreen from "../screens/SignatureScreen";
import LastSignedFullWaiverScreen from "../screens/LastSignedFullWaiverScreen";
import SuccessScreen from "../screens/SuccessScreen";
import WaiverDisabled from "../screens/WaiverDisabled";
import GuardianRejected from "../screens/GuardianRejected";
import MinorDisallowedSreen from "../screens/MinorDisallowedSreen";
import { STEPS, getDeviceId  } from "../constants/steps";

import WaiverError from "../screens/WaiverError";


export default function WaiverFlow(){

    const {
        businessLocationSlug,
        businessTypeSlug,
        serviceSlug,
        guardianToken,
    }=useParams(); //two routes open this url, If guardianToken exists, you're in guardian mode. Otherwise customer mode.    
    
    const location = useLocation();

    const [step,setStep]=useState(null); // isnted use from flowContext.currentStep
    const [loading, setLoading] = useState(false);
    const [initializationError, setInitializationError] = useState(null);
    const [error , setError] = useState(null);
    const [showBackWarning, setShowBackWarning] = useState(false);
    const allowBackNavigationRef = useRef(false);

    const BACK_PROTECTED_STEPS = new Set([
        STEPS.OTP,
        STEPS.PROFILE_PICKER,
        STEPS.EDIT_PROFILE,
        STEPS.GUARDIAN_CONSENT_REQUIRED,
        STEPS.GUARDIAN_DETAILS,
        STEPS.GUARDIAN_WAITING,
        STEPS.GUARDIAN_CONSENT,
        STEPS.ELIGIBILITY_QUESTIONS,
        STEPS.ELIGIBILITY_WARNING,
        STEPS.ELIGIBILITY_DISALLOWED,
        STEPS.ACTUAL_WAIVER,
        STEPS.WAIVER_SIGNATURE,        
        STEPS.GUARDIAN_OTP,
    ]);

    const shouldProtectBack = BACK_PROTECTED_STEPS.has(step);

    // =====================================================
    // PREVENT BROWSER BACK/FORWARD DURING WAIVER FLOW
    // =====================================================
    useEffect(() => {
        if (!shouldProtectBack) {
            return;
        }

        const currentUrl = window.location.href;

        // Create a history entry that represents the current
        // waiver state.
        window.history.pushState(
            { waiverFlow: true },
            "",
            currentUrl
        );

       const handlePopState = () => {

            if (allowBackNavigationRef.current) {
                allowBackNavigationRef.current = false;
                return;
            }

            window.history.pushState(
                { waiverFlow: true },
                "",
                currentUrl
            );

            setShowBackWarning(true);
        };

        window.addEventListener("popstate", handlePopState);

        return () => {
            window.removeEventListener("popstate", handlePopState);
        };
    }, [shouldProtectBack]);

    //optional refresh protection below
    //comment below if accidentaly page refresh is to be allowed
    useEffect(() => {
        if (!shouldProtectBack) {
            return;
        }

        const handleBeforeUnload = (event) => {
            if (allowBackNavigationRef.current) {
                return;
            }

            event.preventDefault();
            event.returnValue = "";
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [shouldProtectBack]);
    //end of comment
        
    //flowData = everything the user enters (phone, DOB, signature, answers).
    //The screens will mostly edit this data
    const [flowData,setFlowData]=useState({
        business:null,
        location:null,
        service:null,
        phone:"",
        lastName:"",
        profile:null,
        dob:null,
        isMinor:false,
        guardian:null,
        waiverVersion:null,
        eligibilityAnswers:{},
        signature:null
    });   

    const updateFlowData=(values)=>{
        setFlowData(prev=>({
            ...prev,
            ...values
        }));
    };

    //flowContext = everything the backend returns (waiver version, customerProfiles, guardian status, business info).
    //The screens will mostly read this data
    const [flowContext, setFlowContext] = useState({
        // Flow
        flowId: null,
        currentStep: null,
        flowType: null,  //CUSTOMER or GUARDIAN
        flowStatus: null, //for messages like NO_ACTIVE_WAIVER

        // Authentication - not required till otp step
        customerProfileId: null,
        customerToken: null,
        guardianToken: null,

        // Business context
        business: null,
        location: null,
        businessType: null,

        // Route information
        businessLocationSlug: null,
        businessTypeSlug: null,

        // Flow data returned by backend
        services: [],
        waiver: null,
        customerProfiles: [],
        guardianStatus: null,
    });

    const updateFlowContext=(values)=>{
        setFlowContext(prev=>({
            ...prev,
            ...values
        }));
    };   
  
    //You actually need both below refs, because they solve different problems:
    // Prevent duplicate initialization requests
    const initializeKeyRef = useRef(null);
    // Ignore responses from older initialization requests
    const initializeRequestId = useRef(0);

    //on any url parameter change, intialize the flow again
    useEffect(() => {
        const initializeKey =
            `${location.key}|${guardianToken || "CUSTOMER"}|${businessLocationSlug}|${businessTypeSlug}|${serviceSlug}`;
        if (initializeKeyRef.current === initializeKey) {
            return;
        }
        initializeKeyRef.current = initializeKey;
        initializeFlow();
    }, [
        location.key,
        guardianToken,
        businessLocationSlug,
        businessTypeSlug,
        serviceSlug
    ]);

    //This means every time you call: setStep(STEPS.PHONE);,   flowContext.currentStep will automatically stay in sync. Later remove setStep
    useEffect(() => {
        setFlowContext(prev => ({
            ...prev,
            currentStep: step,
        }));
    }, [step]);   

    //common code in initializeFlow() and in next() functions
    //This works for every request after initialization.
    const getCustomerSessionToken = () => {
        const businessId =
            flowContext.business?._id ??
            localStorage.getItem(
                `businessId_${businessLocationSlug}`
            ); //Now even after a refresh you can send the JWT on the very first request.
        if (!businessId) {
            return null;
        }
        return localStorage.getItem(
            `customerAuth_${businessId}`
        );
    };
    const getGuardianSessionToken = () => {
        const businessId =
            flowContext.business?._id ??
            localStorage.getItem(
                `businessId_${businessLocationSlug}`
            ); //Now even after a refresh you can send the JWT on the very first request.
        if (!businessId) {
            return null;
        }
        return localStorage.getItem(
            `guardianAuth_${businessId}`
        );
    };
    const createHeaders = () => {
        const headers = {
            "Content-Type":"application/json",
            "x-frontend-url":window.location.pathname,
            "x-device-id":getDeviceId()
        };

        if (!guardianToken) {
            const token = getCustomerSessionToken();
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }
        }
        if (guardianToken) {
            const token = getGuardianSessionToken();
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }
        }
        /* const token = getCustomerSessionToken();
        if(token){
            headers.Authorization=`Bearer ${token}`;
        }
        if (flowContext.customerToken && token != flowContext.customerToken) {
            headers.Authorization = ``; //reset it
            setError("Authorization error");
            return;
        } */
        return headers;
    };
    const sendFlowRequest = async (request) => {
        
        const signingChannel =
            new URLSearchParams(location.search)
                .get("signingChannel")
                ?.toUpperCase() === "QR"
                ? "QR"
                : "DIRECT_LINK";

        const requestWithSigningChannel = {
            ...request,
            signingChannel
        };

        const res = await fetch(
            API_URL.CUSTOMER_START_WAIVER,
            {
                method: "POST",
                headers: createHeaders(),
                body: JSON.stringify(requestWithSigningChannel)
            }
        );

        const response = await res.json();

        if (!res.ok || response.success === false) {
            throw new Error(response.message || "Request failed.");
        }

        return response;
    };
    const applyFlowResponse = (response) => {       

        if (!response.success) {
            return;
        }//This prevents future crashes if another backend error slips through. applyFlowResponse() should only ever receive successful responses.

       
        updateFlowContext({
            ...response,
            business:response.business,
            location:response.location,
            businessType:response.businessType,
            services:response.services,            
            businessLocationSlug,
            businessTypeSlug,
            serviceSlug: response.selectedService?.uniqueSlug ?? serviceSlug,    
            
            guardianToken: response.guardianToken ?? guardianToken,

            //only seems in next() function sometimes
            waiver: response.waiver,
            customerPhone: response.customerPhone,
            profile: response.profile,
            customerProfiles: response.customerProfiles,
            flowState: response.flowState 
        });
        if(response.flowData){
            updateFlowData({
                ...response.flowData,
                action:undefined //reset action on any response.
            });
        }
        if(response.selectedService){
            updateFlowData({
                service:response.selectedService
            });
        }
        if (
            businessLocationSlug &&
            response.business &&
            response.business._id
        ) {
            //cache it once: for initial request - link businssLocationSlug to business_id from response
            localStorage.setItem(
                `businessId_${businessLocationSlug}`,
                response.business?._id
            );
        }
        
        if(response.customerToken){
            localStorage.setItem(
                `customerAuth_${response.business._id}`,
                response.customerToken
            );
        }
        
        if (response.guardianSessionToken && response.business?._id) {
            localStorage.setItem(
                `guardianAuth_${response.business._id}`,
                response.guardianSessionToken
            );
        }
        setStep(response.currentStep);
    };
    //end of common code for both initializeFlow and next()

    //Start the waiver on any URL change.
   const initializeFlow = async () => {

        const requestId = ++initializeRequestId.current;

        setLoading(true);
        setInitializationError(null);

        try {
            let request;
            if (guardianToken) {
                request = {
                    flowType: "GUARDIAN",
                    guardianToken,
                    businessLocationSlug,
                    businessTypeSlug,
                    serviceSlug
                };
            } else {
                request = {
                    flowType: "CUSTOMER",
                    businessLocationSlug,
                    businessTypeSlug,
                    serviceSlug
                };
            }
            const response = await sendFlowRequest(request);
            // Ignore response if a newer URL initialization
            // has already started.
            if (requestId !== initializeRequestId.current) {
                return;
            }
            applyFlowResponse(response);
        } catch (error) {
            // Ignore errors from stale requests.
            if (requestId !== initializeRequestId.current) {
                return;
            }
            console.error(error);
            setInitializationError(
                error.message || "Something went wrong."
            );
        } finally {
            // Only the latest request controls loading state.
            if (requestId === initializeRequestId.current) {
                setLoading(false);
            }
        }
    };
    
    /* Each screen will first call: 
        updateFlowData({
            phone,
            lastName
        });         
        and then call        
        next(); 
    */
    const next = async (overrideData = {}) => {    

        //overrideData is request-specific and hence do not call updateFlowData with overrideData

        const currentFlowType =
            flowContext?.flowType || "CUSTOMER";

        const currentGuardianToken =
            flowContext?.guardianToken || null;

        setLoading(true);
        setError("");
        try {        

            const request = {
                flowId: flowContext.flowId,
                currentStep: step,            // frontendStep → what the frontend says it is currently submitting, whereas currentStep → the persisted step inside CustomerWaiverFlow.
                //flowType: flowContext.flowType,
                flowType: currentFlowType,
                ...(currentFlowType === "GUARDIAN" && {
                    guardianToken: currentGuardianToken
                }),
                businessLocationSlug: flowContext.businessLocationSlug,
                businessTypeSlug: flowContext.businessTypeSlug,
                serviceSlug: flowContext.serviceSlug, //That makes the route metadata immutable for the lifetime of the flow.
                data: {
                    ...flowData,
                    ...overrideData
                }  //always send data to the current step. backend function validator extracts it to varaible flowData again.
            };

            const response = await sendFlowRequest(request);        

            //save kast customer verified for next times easy usage.
            if (
                overrideData.action === "verify_otp" &&
                response?.success
            ) {
                localStorage.setItem(
                    "dys_last_customer_details",
                    JSON.stringify({
                        phone: flowData.phone,
                        lastName: flowData.lastName,
                    })
                );
            }

            
            if (
                overrideData.action === "select_profile" &&
                response?.success
            ) {

                const selectedProfile =
                    response?.profile ||
                    response?.flowData?.profile;

                if (selectedProfile?.lastName) {
                    const savedDetails = JSON.parse(
                        localStorage.getItem("dys_last_customer_details") || "{}"
                    );

                    localStorage.setItem(
                        "dys_last_customer_details",
                        JSON.stringify({
                            ...savedDetails,
                            //phone: flowData.phone,  //keep existing phone number on selecting a new profile
                            lastName: selectedProfile.lastName,
                        })
                    );
                }
            }

            
            if (
                ["save_only_name", "save_profile"].includes(overrideData.action) &&
                response?.success
            ) {
                const updatedProfile =
                    response?.profile ||
                    response?.flowData?.profile ||
                    overrideData.profile;

                if (updatedProfile?.lastName?.trim()) {
                    const savedDetails = JSON.parse(
                        localStorage.getItem("dys_last_customer_details") || "{}"
                    );

                    localStorage.setItem(
                        "dys_last_customer_details",
                        JSON.stringify({
                            ...savedDetails,
                            lastName: updatedProfile.lastName.trim(),
                        })
                    );
                }
            }



            applyFlowResponse(response);

            return response; //used in GuardianWating.jsx 
        }
        catch (err) {
            console.error(err);            
            setError(err.message || "Something went wrong.");
            //throw err;  //for invalid otp also throws error then
        }
        finally {
            setLoading(false);
        }
    };

    const back=()=>{
        //Will implement later
    };   

    const renderStep=()=>{

        //the main function. Depending upon the set step, the screen is called.
        const commonProps={
            flowData,
            updateFlowData,
            flowContext,
            updateFlowContext,
            next,
            back,
            loading,
            setLoading,
            error,
            setError,
        };
        
        switch(step){

            case STEPS.SERVICE:
                return <ServiceSelection {...commonProps} />

            case STEPS.PHONE:
                return <PhoneScreen  {...commonProps} />

            case STEPS.OTP:
                return <OtpScreen {...commonProps} />

            case STEPS.PROFILE_PICKER:
                return <ProfilePicker  {...commonProps} />

            case STEPS.EDIT_PROFILE:
                return <DobScreen {...commonProps} />   

            case STEPS.GUARDIAN_CONSENT_REQUIRED:
                return <GuardianConsentRequiredScreen {...commonProps} />

            case STEPS.GUARDIAN_DETAILS:
                return <GuardianDetailsScreen {...commonProps} />

            case STEPS.GUARDIAN_WAITING:
                return <GuardianWaiting {...commonProps} />


            case STEPS.GUARDIAN_PHONE:
                return <GuardianPhoneScreen  {...commonProps} />

            case STEPS.GUARDIAN_OTP:
                return <GuardianOtpScreen  {...commonProps} />


            case STEPS.GUARDIAN_CONSENT:
                return <GuardianConsentScreen {...commonProps} />

            case STEPS.ELIGIBILITY_QUESTIONS:
                return <EligibilityQuestionsScreen {...commonProps} />

            case STEPS.ELIGIBILITY_WARNING:
                return <EligibilityWarningScreen {...commonProps} />
            
            case STEPS.ELIGIBILITY_DISALLOWED:
                return <EligibilityDisallowedScreen {...commonProps} />

            case STEPS.ACTUAL_WAIVER:
                return <WaiverScreen {...commonProps} />

            case STEPS.WAIVER_SIGNATURE:
                return <SignatureScreen {...commonProps} />

            case STEPS.VIEW_LAST_SIGNED_WAIVER:
                return <LastSignedFullWaiverScreen {...commonProps} />

            case STEPS.WAIVER_SUCCESS:
                return <SuccessScreen {...commonProps} />

            case STEPS.DISABLED:
                return <WaiverDisabled {...commonProps} />

            case STEPS.GUARDIAN_REJECTED:
                return <GuardianRejected {...commonProps} />;

            case STEPS.MINOR_NOT_ALLOWED:
                return <MinorDisallowedSreen {...commonProps} />;


            default:
                return null;
        }
    };     
        
    if (initializationError) {
        return (
            <WaiverError
                flowData={flowData}
                updateFlowData={updateFlowData}
                flowContext={flowContext}
                updateFlowContext={updateFlowContext}
                next={next}
                back={back}
                loading={loading}
                setLoading={setLoading}
                initializationError={initializationError}
                setInitializationError={setInitializationError}
            />
        );
    }

    return (
        <>
            {renderStep()}

            {showBackWarning && (
                <div className="navigation-warning-overlay">
                    <div className="navigation-warning-modal">

                        <h3>Leave this waiver?</h3>

                        <p>
                            Your waiver is currently in progress.
                            If you leave this page, you may lose your current progress.
                        </p>

                        <div className="navigation-warning-actions">

                            <button
                                type="button"
                                onClick={() => {
                                    setShowBackWarning(false);
                                }}
                            >
                                Stay Here
                            </button>

                            <button
                                type="button"
                               onClick={() => {
                                    setShowBackWarning(false);
                                    allowBackNavigationRef.current = true;
                                    window.history.go(-2);
                                }}
                            >
                                Leave
                            </button>

                        </div>

                    </div>
                </div>
            )}
        </>
    );
}